import React, { useState } from 'react';
import {
  HeroSection,
  ToolInterface,
  EmpowermentBanner,
  HowItWorks,
  WorkerQASection,
  FAQWithJustice,
  ActionItems,
} from './sections';
import {
  DividerSolidBand,
  DividerGradient,
  DividerWave,
  DividerQuote,
  DividerAccentLine,
  DividerStats,
} from './sections/dividers';
import { ResultsModal } from './ResultsModal';
import { LanguageToggle } from './common';
import { stateOptions } from '../constants';
import './SinglePageLayout.css';

/**
 * DIVIDER OPTIONS - Change this to switch between divider styles:
 * 'solid'    - Navy band with "Have Questions?" text
 * 'gradient' - Subtle gradient fade
 * 'wave'     - SVG wave/curve shape
 * 'quote'    - Quote strip with testimonial
 * 'line'     - Simple accent line with diamond
 * 'stats'    - Stats bar (15 states, 100% anonymous, Free)
 * 'none'     - No divider
 */
const ACTIVE_DIVIDER = 'line';

/**
 * SinglePageLayout - Main container for the redesigned single-page experience
 *
 * Props:
 * - fileUpload: Object from useFileUpload hook containing:
 *   - file, status, textInput, textUploaded
 *   - report, styledReport, disclaimer, containsNDA, ndaReport
 *   - handleFileChange, handleTextChange, handleTextSubmit, handleUpload, reset
 * - progressMessage: string - Current progress message during processing
 *
 * State:
 * - selectedState: Currently selected state option
 * - disclaimerChecked: Whether user accepted the inline disclaimer
 * - showModal: Whether results modal is visible
 */
export function SinglePageLayout({ fileUpload, progressMessage }) {
  const [selectedState, setSelectedState] = useState(stateOptions[0]);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleStateChange = (option) => {
    setSelectedState(option);
  };

  const handleAnalyze = () => {
    if (fileUpload.file) {
      fileUpload.handleUpload(selectedState);
    }
  };

  const handleViewReport = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleRestart = () => {
    setSelectedState(stateOptions[0]);
    setDisclaimerChecked(false);
    setShowModal(false);
    fileUpload.reset();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="single-page-layout">
      {/* Hero with language toggle overlay */}
      <div className="hero-with-language">
        <LanguageToggle />
        <HeroSection />
      </div>

      {/* Knowledge is Power section - explains the tool */}
      <EmpowermentBanner />

      {/* How It Works - 3 step process */}
      <HowItWorks />

      {/* Upload/Analysis Tool Interface */}
      <ToolInterface
        selectedState={selectedState}
        onStateChange={handleStateChange}
        file={fileUpload.file}
        onFileChange={fileUpload.handleFileChange}
        onFileRemove={fileUpload.handleFileRemove}
        textInput={fileUpload.textInput}
        onTextChange={fileUpload.handleTextChange}
        onTextSubmit={fileUpload.handleTextSubmit}
        textUploaded={fileUpload.textUploaded}
        status={fileUpload.status}
        progressMessage={progressMessage}
        disclaimerChecked={disclaimerChecked}
        onDisclaimerChange={setDisclaimerChecked}
        onAnalyze={handleAnalyze}
        onViewReport={handleViewReport}
        onRestart={handleRestart}
      />

      {/* Q&A Section with worker scenarios - temporarily hidden */}
      {/* <WorkerQASection /> */}

      {/* Divider between Q&A and FAQ - change ACTIVE_DIVIDER at top of file to switch */}
      {ACTIVE_DIVIDER === 'solid' && <DividerSolidBand />}
      {ACTIVE_DIVIDER === 'gradient' && <DividerGradient />}
      {ACTIVE_DIVIDER === 'wave' && <DividerWave />}
      {ACTIVE_DIVIDER === 'quote' && <DividerQuote />}
      {ACTIVE_DIVIDER === 'line' && <DividerAccentLine />}
      {ACTIVE_DIVIDER === 'stats' && <DividerStats />}

      {/* FAQ Section */}
      <FAQWithJustice />

      {/* Action Items Section */}
      <ActionItems />

      {showModal && (
        <ResultsModal
          report={fileUpload.report}
          styledReport={fileUpload.styledReport}
          disclaimer={fileUpload.disclaimer}
          containsNDA={fileUpload.containsNDA}
          ndaReport={fileUpload.ndaReport}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
